import React, {PropsWithChildren, useCallback} from "react";
import {Rnd} from "react-rnd";
import './WindowBorder.css';
import {usePopup} from 'util/hooks/usePopup';
import {log} from 'common/log';
import {ModalFullScreen, ModalPosition, ModalSize} from 'module/Modal/reducer/ModalType';

const resizeHandleClasses = {
	topLeft: 'WindowCorner',
	topRight: 'WindowCorner',
	bottomLeft: 'WindowCorner',
	bottomRight: 'WindowCorner',
};

const enableResizing = {
	topLeft: true,
	topRight: true,
	bottomLeft: true,
	bottomRight: true,
};
const borderSize = 10;
const borderPos = -9;
const resizePos = -7;
const otherSize = '100%';
const n = {width: otherSize, height: borderSize, left: 0, top: borderPos};
const w = {width: borderSize, height: otherSize, top: 0, left: borderPos};
const e = {width: borderSize, height: otherSize, top: 0, right: borderPos};
const s = {width: otherSize, height: borderSize, left: 0, bottom: borderPos};
const resizeHandleStyles = {
	topLeft: {height: borderSize, width: borderSize, top: resizePos, left: resizePos},
	topRight: {height: borderSize, width: borderSize, top: resizePos, right: resizePos},
	bottomLeft: {height: borderSize, width: borderSize, bottom: resizePos, left: resizePos},
	bottomRight: {height: borderSize, width: borderSize, bottom: resizePos, right: resizePos},
};

const maxSize = {
	maxWidth: '99vw',
	maxHeight: '99vh',
	overflow: 'visible',
}
const fullScreenSize = {
	maxWidth: '100%',
	maxHeight: '100%',
	overflow: 'visible',
}

interface Props {
	modalKey: string
	defaultPosition: ModalPosition
	defaultSize: ModalSize
	minWidth: number
	minHeight: number
	onChangedPositionSize: (position?: ModalPosition, size?: ModalSize) => void
	defaultFullScreen?: ModalFullScreen
}

export const WindowBorder = React.memo(({
	modalKey,
	defaultPosition, defaultSize,
	minWidth, minHeight,
	onChangedPositionSize,
	children,
	defaultFullScreen,
}: PropsWithChildren<Props>) => {

	const {setPosition, setSize, position, size} = usePopup(
		defaultPosition,
		defaultSize,
		minWidth,
		minHeight,
	);
	const {fullScreen} = defaultFullScreen || {};

	const onDragStop = useCallback((evt, {x, y}) => {
		const position = {x, y};
		setPosition(position);
		if (onChangedPositionSize) onChangedPositionSize(position);
		log.debug(position);
	}, [onChangedPositionSize, setPosition]);

	const onResizeStop = useCallback((evt, dir, ref, delta, {x, y}) => {
		const position = {x, y};
		const size = {width: ref.offsetWidth, height: ref.offsetHeight};
		setPosition(position);
		setSize(size);
		if (onChangedPositionSize) onChangedPositionSize(position, size);
		log.debug(position, size);
	}, [onChangedPositionSize, setPosition, setSize]);


	return (fullScreen && <div style={fullScreenSize} className={'WindowBorderWrapper'}>
			{children}
		</div> ||
		<Rnd
			key={modalKey}
			style={{outline: 'none'}}
			dragHandleClassName="WindowBorder"
			resizeHandleClasses={resizeHandleClasses}
			resizeHandleStyles={resizeHandleStyles}
			enableResizing={enableResizing}
			bounds={'window'}
			{...maxSize}
			default={{...position, ...size}}
			onDragStop={onDragStop}
			onResizeStop={onResizeStop}
		>
			<div style={maxSize} className={'WindowBorderWrapper'}>
				{children}
			</div>
			<div style={n} className="WindowBorder WindowBorderN"/>
			<div style={w} className="WindowBorder WindowBorderW"/>
			<div style={e} className="WindowBorder WindowBorderE"/>
			<div style={s} className="WindowBorder WindowBorderS"/>
		</Rnd>
	);
});